import React from "react";
import {
  authMiddleware,
  getCurrentUser,
  loginMoodle /* requestAutoLogout */,
  updateUserPicture,
  uploadFile,
} from "../services/moodle/userauth";
import { STRINGS } from "../utils/strings";
import dic from "../utils/languages.json";
import { useConfig } from "./config";

export const AuthContext = React.createContext();

export const appendTokenToUrl = (url, token) => {
  if (!url) return url;

  const parsedUrl = new URL(url);

  parsedUrl.pathname = parsedUrl.pathname.replace(
    "/pluginfile.php/",
    "/webservice/pluginfile.php/"
  );

  parsedUrl.searchParams.set("token", token);

  return parsedUrl.toString();
};

/* actions to work on */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const { config } = useConfig();
  const [verified, setVerified] = React.useState(false);
  const [lang, setLang] = React.useState(null);
  const [showProfileModal, setShowProfileModal] = React.useState(false);

  const actions = {
    RefreshAuth: async () => {
      try {
        const token = localStorage.getItem("token");
        const username = localStorage.getItem("username");
        //console.log({ token, username });
        const user = await getCurrentUser({ token, username, baseUrl: config.baseURL, tipddytoken: config.token });
        setUser({
          ...user,
          profileimageurl: appendTokenToUrl(user.profileimageurl, user.token),
          profileimageurlsmall: appendTokenToUrl(user.profileimageurlsmall, user.token),
        });
        setVerified(true);
      } catch (e) {
        console.error("RefreshAuth error, clearing local session:", e);
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        localStorage.removeItem("refresh");
        setUser(null);
        setVerified(true);
        throw new Error(e);
      }
    },
    Authenticate: async () => {
      if (localStorage.getItem("token") && !user) {
        /*         console.log('refreshtoken') */
        return Promise.resolve(await actions.RefreshAuth());
      }
      setVerified(true);
    },
    Login: async function (credentials) {
      try {
        const moodleUserInfo = await loginMoodle({credentials, baseUrl: config.baseURL, tipddytoken: config.token});
        /* const moodleUserInfo = await authMiddleware({
          token,
          username: credentials.username,
        }); */
        setUser({
          ...moodleUserInfo,
          profileimageurl: appendTokenToUrl(moodleUserInfo.profileimageurl, moodleUserInfo.token),
          profileimageurlsmall: appendTokenToUrl(moodleUserInfo.profileimageurlsmall, moodleUserInfo.token),
        });
        setVerified(true);

        if (moodleUserInfo.errorcode === STRINGS.FORCEPASSWORD_ERROR_CODE) {
          localStorage.setItem(
            STRINGS.FORCEPASSWORD_ERROR_CODE,
            STRINGS.FORCEPASSWORD_ERROR_CODE
          );
        }
      } catch (e) {
        throw new Error(e.message);
      }
    },
    login: () => window.location.replace("/"),
    logout: async (moodle = null) => {
      if (user && user.id) {
        localStorage.setItem("newaccess", user.id);
      }
      setUser(null);
      if (moodle.categories || moodle.courses || moodle.grades) {
        moodle.setCourses(null);
        moodle.setCategories(null);
        moodle.setGrades(null);
        moodle.setCalendarEvents(null);
      }
      localStorage.removeItem(STRINGS.FORCEPASSWORD_ERROR_CODE);
      localStorage.removeItem("token");
      localStorage.removeItem("refresh");
      localStorage.removeItem("username");
      localStorage.removeItem("last-access-cmid");
      localStorage.removeItem("sence_session");
      localStorage.removeItem("current_course");
      localStorage.removeItem("last_login_sence");
      localStorage.removeItem("darkMode");
      localStorage.removeItem("idSence");
      localStorage.removeItem("nru");
      localStorage.removeItem("sence_validated");
      localStorage.removeItem("is_guardian");
      // window.location.replace("/");
    },

    fetchDictionary: async () => {
      let url = window.location.href;
      let l = "";
      /* insertar funcion que maneje el idioma */
      if (url.includes("academy.tuclase")) {
        l = "en";
      } else {
        l = "es";
      }
      //console.log(dic[l])
      setLang(dic[l]);
    },
    UpdateProfilePicture: async (file) => {
      try {
        const token = localStorage.getItem("token");
        const uploadRes = await uploadFile({
          token,
          file,
          baseUrl: config.baseURL,
        });
        if (uploadRes && uploadRes.length > 0) {
          const draftitemid = uploadRes[0].itemid;
          const updateRes = await updateUserPicture({
            token,
            userid: user.id,
            draftitemid,
            baseUrl: config.baseURL,
          });
          if (updateRes.success) {
            await actions.RefreshAuth();
            return updateRes;
          }
        }
        return false;
      } catch (e) {
        console.error("Error updating profile picture:", e);
        throw e;
      }
    },
  };

  const value = {
    user,
    setUser,
    verified,
    lang,
    Authenticate: actions.Authenticate,
    Login: actions.Login,
    logout: actions.logout,
    login: actions.login,
    dictionary: actions.fetchDictionary,
    UpdateProfilePicture: actions.UpdateProfilePicture,
    showProfileModal,
    setShowProfileModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => React.useContext(AuthContext);
